import { FC } from 'react';
import { Save, RotateCcw, AlertCircle } from 'lucide-react';
import { cn } from '../../../../utils/cn';

export interface ProfilePageHeaderProps {
  hasChanges: boolean;
  isSaving: boolean;
  onReset: () => void;
  onSave: () => void;
}

export const ProfilePageHeader: FC<ProfilePageHeaderProps> = ({
  hasChanges,
  isSaving,
  onReset,
  onSave,
}) => {
  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Profile</h2>
          <p className="text-gray-600 dark:text-slate-400 mt-1">
            Manage your professional profile and settings
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={onReset}
            disabled={!hasChanges || isSaving}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium',
              'border border-gray-300 dark:border-slate-600',
              'text-gray-700 dark:text-slate-300',
              'hover:bg-gray-50 dark:hover:bg-slate-700',
              'transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <RotateCcw className="w-4 h-4" />
            <span className="hidden sm:inline">Reset</span>
          </button>

          <button
            onClick={onSave}
            disabled={!hasChanges || isSaving}
            className={cn(
              'flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-medium',
              'bg-blue-600 text-white',
              'hover:bg-blue-700',
              'transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Unsaved Changes Banner */}
      {hasChanges && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            You have unsaved changes. Click "Save Changes" to update your profile.
          </p>
        </div>
      )}
    </>
  );
};
